import { useState } from 'react';

import { buildQuery } from '../../shared/queries.ts';
import { useLiveQueries } from '../live.ts';
import type { LiveQuery } from '../live.ts';
import { argsText, queryTokens } from '../query-text.ts';

/**
 * What this page is watching, right now, in full.
 *
 * The strip above says a number; this is the number's contents. It exists because the claim the
 * number makes is the whole point of the demo and is otherwise unfalsifiable: every list, board
 * column, thread, feed and count here is a live query on a database in a Durable Object, and the
 * page holds a dozen of them at once without re-reading any of them.
 *
 * Each row is what was actually leased. The **name and arguments** are what crossed the wire —
 * the page cannot send a query, only name one of `shared/queries.ts`'s and pass arguments the
 * Worker validates — and the **query** under it is what that name built, rendered from the wire
 * AST the lease was minted for. So opening a board and reading this is the one place where
 * "r2db sends only what changed, to exactly this question" can be checked rather than believed.
 */
export function LiveQueries() {
  const queries = useLiveQueries();
  return (
    <section className="queries" aria-label="Live queries">
      <p className="queries-lede">
        Every one of these is open on the database right now. The page named a query and passed
        arguments; r2db leased <em>exactly</em> that query and sends it only what changed.
        Nothing here re-reads.
      </p>
      <ul className="query-list">
        {queries.map(query => <Row key={query.key} query={query} />)}
      </ul>
    </section>
  );
}

function Row({ query }: { query: LiveQuery }) {
  const [open, setOpen] = useState(false);
  const args = argsText(query.args);
  return (
    <li className={`query${open ? ' open' : ''}`}>
      <button type="button" className="query-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className={`query-dot status-${query.status}`} aria-hidden="true" />
        <strong>{query.name}</strong>
        <span className="query-args">{args}</span>
        <span className="micro muted query-rows">
          {query.error ? 'ERROR' : query.status !== 'live' ? query.status.toUpperCase() : query.rows === null ? 'LIVE' : `${query.rows.toLocaleString()} ${query.rows === 1 ? 'ROW' : 'ROWS'}`}
        </span>
        <span className="micro muted" aria-hidden="true">{open ? '↑' : '↓'}</span>
      </button>
      {/* Built here rather than carried in the registry: it is the same pure function of the same
          name and arguments the Worker ran, and nothing needs it until a visitor opens the row.
          The renderer says what each piece IS, so colouring it is a lookup rather than a second,
          weaker parse of the text it just produced — which would mis-read a project id or a search
          term containing `and`, `issue` or a quote. */}
      {open && (
        <pre className="query-text">
          {queryTokens((buildQuery(query.name, query.args) as { ast(): any }).ast())
            .map((token, i) => (token.kind === 'plain' ? token.text : <span key={i} className={`tok-${token.kind}`}>{token.text}</span>))}
        </pre>
      )}
      {open && query.error && <p className="query-error small">{query.error}</p>}
    </li>
  );
}
